/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import ActionModel, { ActionType } from 'cio/models/action';
import CampaignModel from 'cio/models/campaign';
import CollectionModel from 'cio/models/collection';
import CustomForm from 'cio/models/custom-form';
import FacebookLeadAdsForm from 'cio/models/facebook-lead-ads-form';
import NewsletterModel from 'cio/models/newsletter';
import actionInfo from 'cio/utils/action-info';
import { DataSourceLabelVariant } from 'cio/utils/data-source-label';
import { FormType } from 'cio/utils/forms';

export enum DataSourceType {
  Event = 'event',
  EventAttribute = 'event_attribute',
  Relationship = 'relationship',
  ObjectAttribute = 'object_attribute',
  RelationshipAttribute = 'relationship_attribute',
  TriggerAttribute = 'trigger_attribute',
  DeviceAttribute = 'device_attribute',
  Attribute = 'attribute',
  Campaign = 'campaign',
  Newsletter = 'newsletter',
  Action = 'action',
  Value = 'value',
  Collection = 'collection',
  Form = 'form',
}

export const DATA_SOURCE_TYPE_ICON: Partial<Record<DataSourceType, string>> = {
  [DataSourceType.Event]: 'event',
  [DataSourceType.ObjectAttribute]: 'object',
  [DataSourceType.RelationshipAttribute]: 'connections',
  [DataSourceType.EventAttribute]: 'event',
  [DataSourceType.Relationship]: 'connections',
  [DataSourceType.TriggerAttribute]: 'webhook',
  [DataSourceType.Attribute]: 'person',
  [DataSourceType.Campaign]: 'campaigns',
  [DataSourceType.Newsletter]: 'newsletter',
  [DataSourceType.DeviceAttribute]: 'mobile',
  [DataSourceType.Collection]: 'collection',
  [DataSourceType.Form]: 'form',
};

interface CommonDataSourceLabelArgs {
  type: DataSourceType;
  variant?: DataSourceLabelVariant;
  missingHint?: string;
  icon?: string;
  truncate?: boolean;
}

interface EventDataSourceLabelArgs extends CommonDataSourceLabelArgs {
  type: DataSourceType.Event;
  event?: string;
}

interface RelationshipDataSourceLabelArgs extends CommonDataSourceLabelArgs {
  type: DataSourceType.Relationship;
  objectName?: string;
}

interface EventAttributeDataSourceLabelArgs extends CommonDataSourceLabelArgs {
  type: DataSourceType.EventAttribute;
  attribute?: string;
}

interface TriggerAttributeDataSourceLabelArgs extends CommonDataSourceLabelArgs {
  type: DataSourceType.TriggerAttribute;
  attribute?: string;
}

interface DeviceAttributeDataSourceLabelArgs extends CommonDataSourceLabelArgs {
  type: DataSourceType.DeviceAttribute;
  attribute?: string;
}

interface AttributeDataSourceLabelArgs extends CommonDataSourceLabelArgs {
  type: DataSourceType.Attribute;
  attribute?: string;
}

interface ObjectAttributeDataSourceLabelArgs extends CommonDataSourceLabelArgs {
  type: DataSourceType.ObjectAttribute;
  attribute?: string;
}

interface RelationshipAttributeDataSourceLabelArgs extends CommonDataSourceLabelArgs {
  type: DataSourceType.RelationshipAttribute;
  attribute?: string;
}

interface ValueDataSourceLabelArgs extends CommonDataSourceLabelArgs {
  type: DataSourceType.Value;
  value?: string;
}

interface CampaignDataSourceLabelArgs extends CommonDataSourceLabelArgs {
  type: DataSourceType.Campaign;
  campaign?: CampaignModel;
  name?: string;
}

interface NewsletterDataSourceLabelArgs extends CommonDataSourceLabelArgs {
  type: DataSourceType.Newsletter;
  newsletter?: NewsletterModel;
  name?: string;
}

interface ActionDataSourceLabelArgs extends CommonDataSourceLabelArgs {
  type: DataSourceType.Action;
  action?: ActionModel;
  actionType?: ActionType;
  name?: string;
}

interface CollectionDataSourceLabelArgs extends CommonDataSourceLabelArgs {
  type: DataSourceType.Collection;
  collection?: CollectionModel;
  name?: string;
}

interface FormDataSourceLabelArgs extends CommonDataSourceLabelArgs {
  type: DataSourceType.Form;
  form?: FacebookLeadAdsForm | CustomForm;
  formId?: string;
  formType?: FormType;
}

type DataSourceLabelArgs =
  | EventDataSourceLabelArgs
  | RelationshipDataSourceLabelArgs
  | AttributeDataSourceLabelArgs
  | EventAttributeDataSourceLabelArgs
  | TriggerAttributeDataSourceLabelArgs
  | DeviceAttributeDataSourceLabelArgs
  | ValueDataSourceLabelArgs
  | CampaignDataSourceLabelArgs
  | NewsletterDataSourceLabelArgs
  | ActionDataSourceLabelArgs
  | CollectionDataSourceLabelArgs
  | FormDataSourceLabelArgs
  | ObjectAttributeDataSourceLabelArgs
  | RelationshipAttributeDataSourceLabelArgs;

export default class DataSourceLabel extends Component<DataSourceLabelArgs> {
  readonly DataSourceType = DataSourceType;

  get truncate() {
    return this.args.truncate ?? true;
  }

  get variant() {
    return this.args.variant ?? DataSourceLabelVariant.Dark;
  }

  get icon() {
    if (this.args.icon != null) {
      return this.args.icon;
    }

    // Handle special cases first, for example a campaign which have different
    // icons depending on their type
    switch (this.args.type) {
      case DataSourceType.Campaign:
        return this.args.campaign?.icon ?? DATA_SOURCE_TYPE_ICON[this.args.type];
      case DataSourceType.Relationship:
        return DATA_SOURCE_TYPE_ICON[this.args.type];
      case DataSourceType.Action:
        if (this.args.action != null) {
          return this.args.action.info.icon;
        }

        if (this.args.actionType != null) {
          return actionInfo(this.args.actionType).icon;
        }

        return null;
    }

    return DATA_SOURCE_TYPE_ICON[this.args.type] ?? null;
  }

  get hasIcon() {
    return this.icon != null;
  }

  get label(): DataSourceType | string {
    switch (this.args.type) {
      case this.DataSourceType.Event: {
        return this.isMissing ? 'select event' : this.args.event ?? '';
      }
      case this.DataSourceType.Attribute: {
        return this.isMissing ? 'missing attribute' : this.args.attribute ?? '';
      }
      case this.DataSourceType.EventAttribute: {
        return this.isMissing ? 'missing event attribute' : this.args.attribute ?? '';
      }
      case this.DataSourceType.ObjectAttribute: {
        return this.isMissing ? 'missing object attribute' : this.args.attribute ?? '';
      }
      case this.DataSourceType.RelationshipAttribute: {
        return this.isMissing ? 'missing relationship attribute' : this.args.attribute ?? '';
      }
      case this.DataSourceType.TriggerAttribute: {
        return this.isMissing ? 'missing trigger attribute' : this.args.attribute ?? '';
      }
      case this.DataSourceType.DeviceAttribute: {
        return this.isMissing ? 'missing device attribute' : this.args.attribute ?? '';
      }
      case this.DataSourceType.Campaign: {
        return this.isMissing ? 'select campaign' : this.args.campaign?.name ?? this.args.name ?? '';
      }
      case this.DataSourceType.Newsletter: {
        return this.isMissing ? 'select newsletter' : this.args.newsletter?.name ?? this.args.name ?? '';
      }
      case this.DataSourceType.Action: {
        return this.isMissing ? 'select action' : this.args.name ?? '';
      }
      case this.DataSourceType.Value: {
        return this.args.value ?? '';
      }
      case this.DataSourceType.Collection: {
        return this.isMissing ? 'select collection' : this.args.collection?.get('name') ?? this.args.name ?? '';
      }
      case this.DataSourceType.Relationship: {
        return this.isMissing ? 'select object' : this.args.objectName ?? '';
      }
      default: {
        let unsupportedType = this.args.type;
        throw new Error(`Unsupported type case: ${unsupportedType}`);
      }
    }
  }

  get isMissing() {
    if (this.args.type === DataSourceType.EventAttribute) {
      return this.args.attribute == null;
    }

    if (this.args.type === DataSourceType.ObjectAttribute) {
      return this.args.attribute == null;
    }

    if (this.args.type === DataSourceType.RelationshipAttribute) {
      return this.args.attribute == null;
    }

    if (this.args.type === DataSourceType.TriggerAttribute) {
      return this.args.attribute == null;
    }

    if (this.args.type === DataSourceType.DeviceAttribute) {
      return this.args.attribute == null;
    }

    if (this.args.type === DataSourceType.Campaign) {
      return this.args.campaign == null && this.args.name == null;
    }

    if (this.args.type === DataSourceType.Newsletter) {
      return this.args.newsletter == null && this.args.name == null;
    }

    if (this.args.type === DataSourceType.Collection) {
      return this.args.collection == null && this.args.name == null;
    }

    if (this.args.type === DataSourceType.Action) {
      return this.args.name == null;
    }

    if (this.args.type === DataSourceType.Relationship) {
      return this.args.objectName == null;
    }

    // @ts-ignore: can't index union types well
    return !this.args[this.args.type];
  }

  get variantClasses() {
    let classes = ['font-medium', 'inline-flex', 'items-center', 'gap-0.5', 'max-w-full'];

    if (this.variant === DataSourceLabelVariant.Unstyled) {
      return classes.join(' ');
    }

    classes.push(
      'fly-label',
      'fly-label--lg',
      'leading-[initial]',
      'border',
      'rounded',
      'transition-opacity',
      'border-gray-200',
      'text-base',
      'min-h-[30px]',
      'text-left',
      'break-all',
    );

    if (!this.truncate) {
      classes.push('whitespace-normal', 'overflow-auto');
    }

    if (this.variant === DataSourceLabelVariant.Light) {
      classes.push('bg-white');
    }

    if (this.variant === DataSourceLabelVariant.Dark) {
      classes.push('bg-gray-100');
    }

    return classes.join(' ');
  }
}
